import React, { useState, useEffect, useMemo } from 'react';
import { TextField, Alert, Box, Autocomplete, CircularProgress } from '@mui/material';
import { useUsers } from '../hooks';
import type { User } from '../api/types';

interface UserSelectProps {
  value: string[];
  onChange: (userIds: string[]) => void;
  label?: string;
  placeholder?: string;
  fullWidth?: boolean;
  limit?: number;
  regionIds?: string[];
  depotIds?: string[];
  departmentIds?: string[];
  jobRoleIds?: string[];
  ageRanges?: string[];
  sex?: string[];
  shiftPatterns?: string[];
}

export const UserSelect: React.FC<UserSelectProps> = ({
  value,
  onChange,
  label = 'Select Users',
  placeholder = 'Search and select users...',
  fullWidth = true,
  limit = 20,
  regionIds,
  depotIds,
  departmentIds,
  jobRoleIds,
  ageRanges,
  sex,
  shiftPatterns,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, error } = useUsers({
    search: debouncedSearch,
    limit,
    offset: 0,
    user_ids: value,
    regionIds,
    depotIds,
    departmentIds,
    jobRoleIds,
    ageRanges,
    sex,
    shiftPatterns,
  });

  const users = useMemo(() => data?.data?.users || [], [data]);
  const [selectedUsersData, setSelectedUsersData] = useState<User[]>([]);

  useEffect(() => {
    setSelectedUsersData((prev) => {
      if (!value.length) return prev.length ? [] : prev;

      const retained = prev.filter((user) => value.includes(user.id));
      const toAdd = users.filter(
        (user) => value.includes(user.id) && !retained.some((retainedUser) => retainedUser.id === user.id),
      );

      if (toAdd.length === 0 && retained.length === prev.length) {
        return prev;
      }

      return [...retained, ...toAdd];
    });
  }, [users, value]);

  const allOptions = useMemo(() => {
    const searchUsers = users.filter((user) => !selectedUsersData.find((u) => u.id === user.id));
    return [...selectedUsersData, ...searchUsers];
  }, [users, selectedUsersData]);

  const selectedUsers = useMemo(() => {
    return selectedUsersData.filter((user) => value.includes(user.id));
  }, [selectedUsersData, value]);

  const handleChange = (_event: unknown, newValue: User[]) => {
    onChange(newValue.map((user) => user.id));
    setSelectedUsersData(newValue);
  };

  if (error) {
    return <Alert severity='error'>Failed to load users. Please try again.</Alert>;
  }

  return (
    <Box>
      <Autocomplete
        multiple
        fullWidth={fullWidth}
        options={allOptions}
        value={selectedUsers}
        onChange={handleChange}
        inputValue={searchTerm}
        onInputChange={(_event, newInputValue) => setSearchTerm(newInputValue)}
        getOptionLabel={(option) => option.name || option.email}
        loading={isLoading}
        filterSelectedOptions
        filterOptions={(x) => x}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color='inherit' size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );
};
